import WebsocketClient, {EVENTS} from '@windsor-telecom/contact-centre-websocket-client'
import * as protocol from '@windsor-telecom/contact-centre-websocket-client/src/protocol'
import {SET_WALLBOARD, SET_WALLBOARD_CARDS, UPDATE_WALLBOARD_CARDS} from '@/store/modules/wallboard/mutation-types'
import {CC_WEBSOCKET} from '@/store/static/connection-types'
// eslint-disable-next-line no-unused-vars
import {Store} from 'vuex'
import * as wsMutations from '@/store/modules/websocket/mutation-types'
import {SET_AGENTS, SET_STATUS_LIST, UPDATE_AGENT_SIGNED_IN, UPDATE_AGENTS} from '@/store/modules/wallboard/modules/agents/mutation-types'
import {SET_RETRY_ATTEMPTS} from '@windsor-telecom/customer-portal-framework/src/store/modules/connections/mutation-types'

/**
 * @type {Store}
 */
let store

/**
 * @type {WebsocketClient}
 */
let client

/**
 * @param {array} data
 */
function handleAgentsList (data) {
    store.commit(`wallboard/agents/${SET_AGENTS}`, data.agents)
}

/**
 * Update the is_signed_in flag for the agent in agents store
 * @param {{agentUuid: string}} data
 */
function handleAgentSignedIn (uuid, value = true) {
    store.commit(`wallboard/agents/${UPDATE_AGENT_SIGNED_IN}`, {
        uuid,
        value
    })
}

/**
 * @param {object} data
 */
function handleAgentStatusChange (data) {
    store.commit(`wallboard/agents/${UPDATE_AGENTS}`, [data])
}

/**
 * Set connected state in store
 */
function handleConnected () {
    store.commit(
        `websocket/${wsMutations.SET_CONNECTED}`,
        true
    )

    store.dispatch(`connections/setConnected`, {
        type: CC_WEBSOCKET,
        value: true
    })
}

/**
 * Handle websocket disconnection
 * @param {{
 * event: CloseEvent,
 * error: {
 *  code: number,
 *  message: string,
 *  info: string,
 *  ref: string,
 *  status: number
 * },
 * isAuthClosure: boolean,
 * shouldRetry: boolean,
 * wasConnected: boolean
 * }} data
 * @returns {Promise<any>|void}
 */
function handleDisconnected (data) {
    // Set the websocket to disconnected in the websocket store
    store.commit(
        `websocket/${wsMutations.SET_CONNECTED}`,
        false
    )

    // If we were connected, and have now lost connection then start connections monitor retry interval
    if (data.shouldRetry) {
        return store.dispatch(`connections/setConnected`, {
            type: CC_WEBSOCKET,
            value: false
        })
    }

    // If auth issue then show authentication error
    if (data.isAuthClosure && data.wasConnected) {
        store.dispatch(`connections/resetConnections`)
        // If auth issue then show authentication error
        return store.commit(
            `websocket/${wsMutations.SET_WEBSOCKET_AUTH_ERROR}`,
            data.error
        )
    }

    const connectionStatus = store.getters['connections/connections'][CC_WEBSOCKET]
    const isRetryingConnection = connectionStatus.retryInterval

    // If we lost connection and are not retrying show error
    if (!isRetryingConnection) {
        store.commit(
            `websocket/${wsMutations.SET_WEBSOCKET_AUTH_ERROR}`,
            data.error
        )
    }
}

/**
 * Reset the auth store when the connection is accepted
 */
function handleConnectionAccepted () {
    store.commit(`websocket/${wsMutations.RESET_WEBSOCKET_AUTH}`)
    store.commit(`websocket/${wsMutations.SET_WEBSOCKET_AUTHENTICATED}`, true)
}

/**
 * @param {object} data
 */
function handleStatusList (data) {
    store.commit(`wallboard/agents/${SET_STATUS_LIST}`, data.statuses)
}

/**
 * @param {array} data
 */
function handleUpdateWallboardCards (data) {
    store.commit(`wallboard/${UPDATE_WALLBOARD_CARDS}`, data)
}

/**
 * @param {object} data
 */
function handleWallboardDetails (data) {
    const {uuid, name, cards} = data

    store.commit(`wallboard/${SET_WALLBOARD}`, {uuid, name})
    store.commit(`wallboard/${SET_WALLBOARD_CARDS}`, cards)
}

/**
 * @returns {Promise<void>}
 */
async function processMessage (payload = {}) {
    const {data, name} = payload

    switch (name) {
        case protocol.S2C_ConnectionAccepted:
            return handleConnectionAccepted()
        case protocol.S2C_StatusList:
            return handleStatusList(data)
        case protocol.S2C_WallboardAgentStatusChange:
            return handleAgentStatusChange(data)
        case protocol.S2C_WallboardAgentsList:
            return handleAgentsList(data)
        case protocol.S2C_WallboardCardsUpdate:
            return handleUpdateWallboardCards(data.cards)
        case protocol.S2C_WallboardCardUpdate:
            return handleUpdateWallboardCards([data])
        case protocol.S2C_WallboardDetails:
            return handleWallboardDetails(data)
        case protocol.S2C_AgentSignedIn:
            return handleAgentSignedIn(data.agentUuid, true)
        case protocol.S2C_AgentSignedOut:
            return handleAgentSignedIn(data.agentUuid, false)
        default:
            return null
    }
}

/**
 * Start the WebsocketClient and bind event handlers
 * @param {string} token
 */
function startWebsocket (token) {
    let url = process.env.VUE_APP_CC_WEBSOCKET_URL

    if (token) {
        url += `?auth=${token}`
    }

    client = new WebsocketClient(url)

    // On connected set the connected state in the store
    client.on(EVENTS.CONNECTED, handleConnected)

    // On connected set the connected state in the store
    client.on(EVENTS.DISCONNECTED, handleDisconnected)

    // On message bind the processMessage handler
    client.on(EVENTS.MESSAGE, processMessage)
}

/**
 * Default export is a factory method which is used by the Vuex plugin system
 * When the `SET_WEBSOCKET_AUTH` mutation is called, an attempt to connect the
 * websocket is made.
 */
export default function websocketPlugin () {
    return storeInstance => {
        store = storeInstance

        store.subscribe((mutation) => {
            const {type, payload} = mutation
            switch (type) {
                case `websocket/${wsMutations.SET_WEBSOCKET_AUTH}`:
                    return startWebsocket(mutation.payload)
                case `connections/${SET_RETRY_ATTEMPTS}`:
                    return payload.type === CC_WEBSOCKET
                        && payload.value > 0
                        && client.startConnection()
                default:
                    return null
            }
        })
    }
}
